import { LANGUAGE_LEVEL_LABELS, type CvAnalysis } from "@shared/modules/x-extraction";
import { METHOD_LABELS, formatMonths, fullName, levelLabel, period, warningLabel } from "../lib/profile";

function Tags({ items, variant }: { items: string[]; variant?: "primary" | "info" }) {
  if (!items.length) return <p className="muted">—</p>;
  return (
    <ul className="profil__tags">
      {items.map((t) => (
        <li key={t} className={variant ? `cv-tag cv-tag--${variant}` : "cv-tag"}>
          {t}
        </li>
      ))}
    </ul>
  );
}

/**
 * Fiche synthèse d'un CV analysé : ce qu'un recruteur veut voir d'un coup
 * d'œil (poste, expérience, diplôme, compétences), puis le détail du parcours.
 */
export function ProfileView({ analysis: a }: { analysis: CvAnalysis }) {
  const p = a.profile;
  const place = [p?.city, p?.country].filter(Boolean).join(", ");
  return (
    <div className="profil">
      <div className="profil__head">
        <div className="profil__identity">
          <h3 className="profil__name">{fullName(a)}</h3>
          <p className="profil__title">{p?.title ?? p?.totals.lastTitle ?? "Poste non déterminé"}</p>
          {p?.summary ? <p className="profil__summary">{p.summary}</p> : null}
        </div>
        <dl className="profil__facts">
          <div>
            <dt>Expérience</dt>
            <dd>{formatMonths(p?.totals.experienceMonths)}</dd>
          </div>
          <div>
            <dt>Plus haut diplôme</dt>
            <dd>{levelLabel(p?.totals.highestLevel)}</dd>
          </div>
          <div>
            <dt>Lieu</dt>
            <dd>{place || "—"}</dd>
          </div>
          <div className="profil__fact--wide">
            <dt>Contact</dt>
            <dd>
              {a.identity.email ?? "—"}
              {a.identity.phone ? <span className="profil__phone">{a.identity.phone}</span> : null}
            </dd>
          </div>
        </dl>
      </div>

      {!p ? (
        <p className="muted">Profil détaillé indisponible : seule l'identité a pu être lue ({a.warnings.map(warningLabel).join(", ") || "lecture incomplète"}).</p>
      ) : (
        <>
          {p.strengths.length ? (
            <section className="profil__section">
              <h4 className="profil__heading">Points forts</h4>
              <ul className="profil__strengths">
                {p.strengths.map((s) => (
                  <li key={s}>{s}</li>
                ))}
              </ul>
            </section>
          ) : null}

          <section className="profil__section">
            <h4 className="profil__heading">Expériences</h4>
            {p.experiences.length ? (
              <ol className="profil__timeline">
                {p.experiences.map((e, i) => (
                  <li key={i} className="profil__step">
                    <p className="profil__step-title">
                      <strong>{e.title ?? "Poste"}</strong>
                      {e.employer ? <> — {e.employer}</> : null}
                    </p>
                    <p className="profil__step-meta">
                      {[period(e), e.months !== null ? formatMonths(e.months) : null, e.location].filter(Boolean).join(" · ")}
                    </p>
                    {e.highlights.length ? (
                      <ul className="profil__highlights">
                        {e.highlights.map((h) => (
                          <li key={h}>{h}</li>
                        ))}
                      </ul>
                    ) : null}
                  </li>
                ))}
              </ol>
            ) : (
              <p className="muted">Aucune expérience lue.</p>
            )}
          </section>

          <section className="profil__section">
            <h4 className="profil__heading">Formations</h4>
            {p.education.length ? (
              <ul className="profil__list">
                {p.education.map((e, i) => (
                  <li key={i}>
                    <strong>{e.degree ?? e.field ?? "Formation"}</strong>
                    {e.field && e.field !== e.degree ? ` (${e.field})` : null}
                    <span className="muted">{[e.school, e.location, e.year, e.level ? levelLabel(e.level) : null].filter(Boolean).map((v) => ` · ${v}`)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="muted">Aucune formation lue.</p>
            )}
          </section>

          <div className="profil__columns">
            <section className="profil__section">
              <h4 className="profil__heading">Compétences techniques</h4>
              <Tags items={p.skills.technical} variant="primary" />
            </section>
            <section className="profil__section">
              <h4 className="profil__heading">Outils et logiciels</h4>
              <Tags items={p.skills.tools} variant="info" />
            </section>
            <section className="profil__section">
              <h4 className="profil__heading">Savoir-être</h4>
              <Tags items={p.skills.soft} />
            </section>
            <section className="profil__section">
              <h4 className="profil__heading">Langues</h4>
              <Tags items={p.languages.map((l) => (l.level ? `${l.language} · ${LANGUAGE_LEVEL_LABELS[l.level].toLowerCase()}` : l.language))} />
            </section>
            {p.certifications.length ? (
              <section className="profil__section">
                <h4 className="profil__heading">Certifications</h4>
                <ul className="profil__list">
                  {p.certifications.map((c) => (
                    <li key={c.name}>
                      {c.name}
                      <span className="muted">{[c.issuer, c.year].filter(Boolean).map((v) => ` · ${v}`)}</span>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}
            {p.licenses.length ? (
              <section className="profil__section">
                <h4 className="profil__heading">Permis et cartes</h4>
                <Tags items={p.licenses} />
              </section>
            ) : null}
          </div>
        </>
      )}

      <p className="profil__meta">
        {[
          a.method ? `Lu depuis ${METHOD_LABELS[a.method]}` : null,
          a.durationMs ? `en ${Math.max(1, Math.round(a.durationMs / 1000))} s` : null,
          a.model ? `modèle local ${a.model}` : null,
          a.originalName,
        ]
          .filter(Boolean)
          .join(" · ")}
      </p>
    </div>
  );
}
